import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.js';
import { api } from '../api.js';
import { loadBrandingFromStorage } from '../utils/branding.js';
import {
  FileText, Mail, Lock, Eye, EyeOff, ArrowRight,
  AlertCircle, Check, CheckCircle2, Zap, Shield,
  RefreshCw, Code2, X, Building2,
} from 'lucide-react';
import '../styles/login.css';

function DecoCardA() {
  return (
    <div className="lp-deco-card lp-deco-card--a">
      <div className="lp-deco-card__label">Render completado</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: '0.78rem', color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>invoice-INV-097.pdf</span>
        <span style={{ fontSize: '0.68rem', color: '#4ade80', display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 6px rgba(74,222,128,0.7)' }} />
          1.2s
        </span>
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        {[70, 85, 60, 90, 75, 95, 80, 100, 88, 92].map((h, i) => (
          <div key={i} style={{ flex: 1, height: `${h * 0.28}px`, borderRadius: 2, background: i > 7 ? 'rgba(79,70,229,0.7)' : 'rgba(51,65,85,0.6)', transition: 'height 0.3s' }} />
        ))}
      </div>
      <div style={{ fontSize: '0.68rem', color: '#334155', marginTop: 6 }}>hace 2 min · tenant_acme</div>
    </div>
  );
}

function DecoCardB() {
  return (
    <div className="lp-deco-card lp-deco-card--b">
      <div className="lp-deco-card__label">API Gateway · P50</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#F8FAFC', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '-0.02em' }}>42ms</span>
        <span style={{ fontSize: '0.68rem', color: '#4ade80' }}>↓ 3ms</span>
      </div>
      <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 28 }}>
        {[12, 18, 14, 22, 16, 24, 20, 18, 26, 22, 28, 24].map((h, i) => (
          <div key={i} style={{ flex: 1, height: `${h}px`, borderRadius: 2, background: i === 11 ? '#22D3EE' : `rgba(34,211,238,${0.2 + i * 0.05})` }} />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
        <div className="lp-deco-pulse" />
        <span style={{ fontSize: '0.68rem', color: '#475569' }}>Todos los sistemas operando</span>
      </div>
    </div>
  );
}

const PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  id: i,
  x: 10 + Math.abs(Math.sin(i * 137.5) * 80),
  y: 10 + Math.abs(Math.cos(i * 137.5) * 80),
  size: 1.5 + (i % 3),
  color: i % 3 === 0 ? '#4F46E5' : i % 3 === 1 ? '#22D3EE' : '#8B5CF6',
  opacity: 0.15 + (i % 5) * 0.07,
}));

function validateEmail(v) {
  if (!v) return 'El email es obligatorio.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Ingresa un email válido.';
  return '';
}

function validatePassword(v) {
  if (!v) return 'La contraseña es obligatoria.';
  if (v.length < 6) return 'Mínimo 6 caracteres.';
  return '';
}

export default function LoginPage() {
  const branding = loadBrandingFromStorage();
  const appName = branding.appName || 'ReportEngine';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [emailErr, setEmailErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [formState, setFormState] = useState('idle');
  const [serverErr, setServerErr] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);

  const emailRef = useRef(null);
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  useEffect(() => {
    emailRef.current?.focus();
    const rememberedEmail = localStorage.getItem('aic_remember_email') || '';
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRemember(true);
    }
  }, []);

  useEffect(() => {
    if (emailTouched) setEmailErr(validateEmail(email));
  }, [email, emailTouched]);

  useEffect(() => {
    if (pwTouched) setPasswordErr(validatePassword(password));
  }, [password, pwTouched]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailTouched(true);
    setPwTouched(true);

    const eErr = validateEmail(email);
    const pErr = validatePassword(password);
    setEmailErr(eErr);
    setPasswordErr(pErr);
    if (eErr || pErr) return;

    setFormState('loading');
    setServerErr('');

    try {
      const res = await api.login(email, password);
      setAuth(res.token, res.user);
      if (remember) localStorage.setItem('aic_remember_email', email);
      else localStorage.removeItem('aic_remember_email');
      setFormState('success');
      setTimeout(() => navigate('/templates'), 1100);
    } catch (err) {
      setFormState('error');
      setServerErr(err?.message || 'No fue posible iniciar sesión.');
    }
  };

  const isLoading = formState === 'loading';

  return (
    <div className="lp-page">
      <div className="lp-brand">
        {PARTICLES.map((p) => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              background: p.color,
              opacity: p.opacity,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        ))}

        <Link to="/" className="lp-brand__top">
          <div className="lp-brand__logo">
            <FileText size={16} color="white" />
          </div>
          <span className="lp-brand__name">{appName}</span>
        </Link>

        <div className="lp-brand__hero">
          <div className="lp-brand__badge">
            <span className="lp-brand__badge-dot" />
            Plataforma de reportes v2.0
          </div>

          <h2 className="lp-brand__headline">
            Genera documentos
            <br />
            <span>PDF, imagen y HTML</span>
            <br />
            a escala.
          </h2>

          <p className="lp-brand__sub">
            Conecta tus datos, elige tu plantilla y obtén documentos de producción en milisegundos. Todo vía API o desde el dashboard.
          </p>

          <div className="lp-features">
            {[
              {
                cls: 'c1', Icon: Zap,
                title: 'Render en menos de 2s',
                desc: 'Chrome headless optimizado para PDFs complejos de múltiples páginas.',
              },
              {
                cls: 'c2', Icon: Code2,
                title: 'API REST con JWT',
                desc: 'Integración directa desde cualquier stack. SDKs en Node, Python y PHP.',
              },
              {
                cls: 'c3', Icon: RefreshCw,
                title: 'Renders asíncronos',
                desc: 'Cola con prioridad y deduplicación. Ideal para alto volumen.',
              },
            ].map(({ cls, Icon, title, desc }) => (
              <div key={title} className="lp-feature-item">
                <div className={`lp-feature-icon ${cls}`}><Icon size={13} /></div>
                <div className="lp-feature-text">
                  <strong>{title}</strong>
                  <span>{desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lp-stats">
          {[
            { value: '99.9%', label: 'Uptime SLA' },
            { value: '<2s', label: 'Render PDF P50' },
            { value: '10M+', label: 'Docs generados' },
          ].map((s) => (
            <div key={s.label}>
              <div className="lp-stat__value">{s.value}</div>
              <div className="lp-stat__label">{s.label}</div>
            </div>
          ))}
        </div>

        <DecoCardA />
        <DecoCardB />
      </div>

      <div className="lp-form-panel">
        <div className="lp-form-box">
          <div className="lp-form-header">
            <div className="lp-form-header__logo"><FileText size={22} color="white" /></div>
            <h1>Iniciar sesión</h1>
            <p>{formState === 'success' ? 'Autenticación exitosa' : `Bienvenido de vuelta a ${appName}`}</p>
          </div>

          {formState === 'success' ? (
            <div className="lp-success-state">
              <div className="lp-success-ring"><CheckCircle2 size={28} /></div>
              <h3>¡Acceso concedido!</h3>
              <p>Redirigiendo al dashboard…</p>
              <div className="lp-progress-bar"><div className="lp-progress-bar__fill" /></div>
            </div>
          ) : (
            <>
              {formState === 'error' && serverErr && (
                <div className="lp-error-banner" style={{ marginBottom: '1rem' }}>
                  <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{serverErr}</span>
                  <button
                    type="button"
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 2, display: 'flex' }}
                    onClick={() => { setFormState('idle'); setServerErr(''); }}
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              <form className="lp-form" onSubmit={handleSubmit} noValidate>
                <div className="lp-field">
                  <label className="lp-label" htmlFor="lp-email">Correo electrónico</label>
                  <div className="lp-input-wrap">
                    <span className="lp-input-icon"><Mail size={15} /></span>
                    <input
                      id="lp-email"
                      ref={emailRef}
                      type="email"
                      autoComplete="email"
                      placeholder="Correo electrónico"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setEmailTouched(true)}
                      className={`lp-input${emailErr && emailTouched ? ' has-error' : email && !emailErr ? ' has-success' : ''}`}
                      disabled={isLoading}
                    />
                    {email && !emailErr && (
                      <span style={{ position: 'absolute', right: '0.875rem', color: '#4ade80', display: 'flex' }}>
                        <Check size={14} />
                      </span>
                    )}
                  </div>
                  {emailErr && emailTouched && (
                    <span className="lp-field-error"><AlertCircle size={12} />{emailErr}</span>
                  )}
                </div>

                <div className="lp-field">
                  <label className="lp-label" htmlFor="lp-password">Contraseña</label>
                  <div className="lp-input-wrap">
                    <span className="lp-input-icon"><Lock size={15} /></span>
                    <input
                      id="lp-password"
                      type={showPw ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Contraseña"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setPwTouched(true)}
                      className={`lp-input${passwordErr && pwTouched ? ' has-error' : ''}`}
                      style={{ paddingRight: '2.75rem' }}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="lp-pw-toggle"
                      onClick={() => setShowPw((v) => !v)}
                      tabIndex={-1}
                      aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {passwordErr && pwTouched && (
                    <span className="lp-field-error"><AlertCircle size={12} />{passwordErr}</span>
                  )}
                </div>

                <div className="lp-row-opts">
                  <label className="lp-checkbox-label">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    <span className="lp-checkbox-box"><span className="lp-checkbox-check"><Check size={10} strokeWidth={3} /></span></span>
                    Recordarme
                  </label>
                  <a href="#" className="lp-forgot-link">¿Olvidaste tu contraseña?</a>
                </div>

                <button type="submit" className="lp-submit" disabled={isLoading}>
                  {isLoading ? (
                    <><span className="lp-spinner" />Verificando…</>
                  ) : (
                    <>Iniciar sesión<ArrowRight size={15} /></>
                  )}
                </button>

            
              </form>


              <p className="lp-signup-prompt">
                ¿No tienes cuenta?{' '}
                <Link to="/register" style={{ color: '#818cf8', fontWeight: 500, textDecoration: 'none' }}>
                  Crear cuenta gratis
                </Link>
              </p>
            </>
          )}

          <div className="lp-footer-links">
            <Link to="/politica-de-privacidad">Privacidad</Link>
            <span>·</span>
            <Link to="/terminos-de-servicio">Términos</Link>
            <span>·</span>
            <Link to="/estado-del-sistema">Estado del sistema</Link>
            <span>·</span>
            <Link to="/documentacion">Help Center</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
